import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { RichTextEditorComponent, RichTextEmittedData } from 'shared/feature-rich-text-editor';
import { RichTextViewComponent } from 'shared/feature-rich-text-view';
import {
  ButtonDirective,
  ChipComponent,
  ConfirmationDialogComponent,
  DialogService,
  IconDirective,
  MenuComponent,
  MenuItemDirective,
  MenuTriggerDirective,
  TooltipDirective,
} from 'shared/ui-common';
import { AutofocusDirective, isTruthy } from 'shared/util-common';

import { CommentableTextDirective } from '@treetop/shared/comments';
import { Reference } from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType } from '@treetop/shared/reference-numbering';

import { normalizeString } from '../../../../content.util';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-compare-description',
  templateUrl: './compare-description.component.html',
  styleUrls: ['./compare-description.component.scss'],
  imports: [
    RichTextViewComponent,
    MatIconModule,
    RichTextEditorComponent,
    AutofocusDirective,
    IconDirective,
    TooltipDirective,
    TranslocoPipe,

    ChipComponent,
    MenuComponent,
    MenuItemDirective,
    MenuTriggerDirective,
    CommentableTextDirective,
    ButtonDirective,
  ],
})
export class CompareDescriptionComponent {
  private dialogService = inject(DialogService);

  public original = input<string>();
  public local = input<string>();
  public id = input<string>();
  public uniqueId = input<string>();
  public sectionType = input<GuidelineElementSection>();
  public guidelineElementType = input<GuidelineElementType>();
  public compareMode = model(false);
  public mergeConflictStatus = input<'conflict' | 'resolved' | undefined>();
  public editMode = model(false);
  public references = input<Reference[] | undefined>();

  protected showHeader = computed(() => this.mergeConflictStatus() === 'resolved' || this.compareMode());

  public localChanged = output<RichTextEmittedData>();
  public conflictResolved = output();
  public conflictUnresolved = output();

  protected hasLocalChanges = computed(() => {
    const local = this.local();
    const original = this.original();

    return local != null && normalizeString(local) !== normalizeString(original);
  });

  protected revertLocalDescription() {
    this.dialogService
      .open(ConfirmationDialogComponent, {
        title: 'SingleGuidelineManager.ImportedGuideline.Editor.RevertToOriginalConfirmDialog.Title',
        data: {
          message: 'SingleGuidelineManager.ImportedGuideline.Editor.RevertToOriginalConfirmDialog.Message',
          cancelButtonCaption: 'Cancel',
          confirmButtonCaption: 'SingleGuidelineManager.ImportedGuideline.Editor.RevertToOriginalConfirmDialog.Confirm',
        },
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        this.localChanged.emit(undefined);
        this.editMode.set(false);
        this.compareMode.set(false);

        this.conflictResolved.emit();
      });
  }

  protected handleLocalDescriptionChanged(description: string): void {
    this.localChanged.emit({
      text: description,
      references: this.references(),
    });

    if (this.mergeConflictStatus() === 'conflict') {
      this.conflictResolved.emit();
    }
  }

  protected handleLocalDescriptionReferencesChanged(references: Reference[]) {
    this.localChanged.emit({
      text: this.local(),
      references,
    });

    if (this.mergeConflictStatus() === 'conflict') {
      this.conflictResolved.emit();
    }
  }

  protected handleAcceptLocalClicked() {
    if (this.mergeConflictStatus()) {
      this.conflictResolved.emit();
    }
  }

  protected handleAcceptOriginalClicked() {
    this.revertLocalDescription();
  }
}
